import React from 'react';
import { CheckCircle, Award, Users, Target, Shield } from 'lucide-react';
import { motion } from 'framer-motion';

export default function About() {
  
  const values = [
    {
      icon: Award,
      title: 'Excellence',
      description: 'We strive for excellence in every job, using best practices and highest quality standards.'
    },
    {
      icon: Users,
      title: 'Customer First',
      description: 'Your satisfaction is our priority. We listen to your needs and deliver effective solutions.'
    },
    {
      icon: Target,
      title: 'Integrity',
      description: 'Honest, transparent service with fair pricing. We do what we say and say what we do.'
    },
    {
      icon: CheckCircle,
      title: 'Reliability',
      description: 'Count on us to be there when you need us, delivering consistent, dependable service.'
    }
  ];

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen bg-white">
            
      {/* Hero Section */}
      <section className="bg-slate-900 py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            transition={{ duration: 0.4 }}
          >
            <p className="text-white/60 font-medium mb-4 tracking-wide uppercase text-sm">About Total Repair UK</p>
            <h1 className="text-4xl lg:text-5xl font-light text-white mb-4 leading-tight">
              Excellence in
              <span className="block font-semibold">Drainage Services</span>
            </h1>
            <p className="text-slate-400 max-w-3xl mx-auto">
              Professional drainage solutions.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              transition={{ duration: 0.4 }}
            >
              <img 
                src="https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=700&h=700&fit=crop"
                alt="Our team"
                className="shadow-xl"
              />
            </motion.div>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              <p className="text-slate-600 font-medium mb-3 tracking-wide text-sm uppercase">Our Story</p>
              <h2 className="text-3xl font-light text-slate-900 mb-6 leading-tight">
                Building Trust Through
                <span className="block font-semibold">Quality Service</span>
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
Total Repair UK was established with a clear goal: to deliver reliable drainage services
customers can depend on. As a new company, we’re focused on building a strong
reputation through quality work, fair pricing, and dependable service.              </p>
              <p className="text-slate-600 leading-relaxed mb-8">
We invest in modern equipment and ongoing training to ensure every job is completed
efficiently and to a high standard. Our aim is simple: get it right first time and earn your
trust.              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-slate-900 flex-shrink-0" />
                  <span className="text-slate-700">Certified Professionals</span>
                </div>
                <div className="flex items-center gap-3">
                  <Award className="w-5 h-5 text-slate-900 flex-shrink-0" />
                  <span className="text-slate-700">Award Winning Service</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            transition={{ duration: 0.4 }}
            className="text-center mb-16"
          >
            <p className="text-slate-600 font-medium mb-4 tracking-wide text-sm uppercase">Our Values</p>
            <h2 className="text-3xl lg:text-4xl font-light text-slate-900 mb-4">
              What Drives
              <span className="block font-semibold">Our Work</span>
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Our core values guide everything we do, from customer service to technical excellence.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="bg-slate-50 p-6 border-l-4 border-slate-900"
              >
                <div className="w-12 h-12 bg-slate-900 flex items-center justify-center mb-4">
                  <value.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">{value.title}</h3>
                <p className="text-slate-600 leading-relaxed text-sm">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

          </div>
  );
}